import React, { createContext, useReducer, useEffect } from 'react';
import { ThemeQuery, ThemesQuery, useThemeQuery } from '../../generated/graphql';
import { useParams } from 'react-router-dom';
import useThemes from './useThemes';

type Theme = ThemeQuery['theme'];
type FamilyTheme = ThemesQuery['familyThemes'][0];

interface Action {
  families?: FamilyTheme[];
  theme: Theme | null;
}

interface RouteParams {
  id: string;
}

interface State {
  currentTheme: FamilyTheme | null;
  currentColorway: Theme | null;
  previousTheme: Theme | null;
  nextTheme: Theme | null;
  currentThemeIndex: number;
  currentColorwayIndex: number;
  themeCount: number;
}

const initialState: State = {
  currentTheme: null,
  previousTheme: null,
  nextTheme: null,
  currentColorway: null,
  currentThemeIndex: 0,
  currentColorwayIndex: 0,
  themeCount: 0,
};

const getIndices = (currTheme: Theme | null, families: FamilyTheme[]) => {
  return families.reduce<[number, number]>(
    (memo, family, familyIndex) => {
      const colorwayIndex = family.themes?.findIndex(theme => theme.id === currTheme?.id);
      if (colorwayIndex !== undefined && colorwayIndex >= 0) {
        return [familyIndex, colorwayIndex];
      }
      return memo;
    },
    [0, 0]
  );
};

export const getFirstOrNull = (array: any[] | null | undefined) => {
  return array ? array[0] || null : null;
};

const reducer = (prevState: State, { families, theme }: Action): State => {
  const loadedThemes = families || [];
  const [currentThemeIndex, currentColorwayIndex] = getIndices(theme, loadedThemes);
  const currentTheme = loadedThemes[currentThemeIndex];
  const currentColorway = theme;
  const previousTheme = currentThemeIndex > 0 ? getFirstOrNull(loadedThemes[currentThemeIndex - 1].themes) : null;
  const nextTheme =
    currentThemeIndex < loadedThemes.length - 1 ? getFirstOrNull(loadedThemes[currentThemeIndex + 1].themes) : null;

  return {
    ...prevState,
    currentTheme,
    currentColorway,
    previousTheme,
    nextTheme,
    currentThemeIndex,
    currentColorwayIndex,
    themeCount: loadedThemes.length,
  };
};

const ThemeContext = createContext<State>(initialState);

export const ThemeProvider: React.FC = ({ children }) => {
  const { themes, loading: themesLoading } = useThemes();

  const { id } = useParams<RouteParams>();
  const { data: themeData, loading: themeLoading } = useThemeQuery({
    variables: {
      id: parseInt(id),
    },
    skip: !id,
  });
  const [state, dispatch] = useReducer(reducer, initialState);

  useEffect(() => {
    if (!themesLoading && !themeLoading && themeData?.theme) {
      dispatch({
        //@ts-ignore
        families: themes || [],
        theme: themeData?.theme,
      });
    }
  }, [themeData, themes, themesLoading, themeLoading]);

  return <ThemeContext.Provider value={state}>{children}</ThemeContext.Provider>;
};

export default ThemeContext;
